import { useEffect, useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Trophy, Lock, Star, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function fireConfetti() {
  try {
    confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ["#f59e0b", "#2563eb", "#10b981", "#ef4444"] });
    setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0, y: 0.7 } }), 250);
    setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.7 } }), 400);
  } catch { /* canvas unavailable */ }
}

function Badge({ b, isMe, showcased, onToggle, celebrate }) {
  return (
    <div
      onClick={() => isMe && b.unlocked && onToggle?.(b.key)}
      className={`relative rounded-2xl border p-3 flex flex-col items-center text-center gap-1 transition-transform hover:-translate-y-0.5 ${b.unlocked ? "bg-gradient-to-b from-amber-50 to-background border-amber-300/60 dark:from-amber-500/10" : "bg-muted/30 border-border opacity-80"} ${isMe && b.unlocked ? "cursor-pointer" : ""} ${showcased ? "ring-2 ring-primary" : ""} ${celebrate ? "animate-pop-in ring-2 ring-amber-400" : ""}`}
      data-testid={`badge-${b.key}`}
      data-unlocked={b.unlocked ? "1" : "0"}
      data-showcased={showcased ? "1" : "0"}
      title={isMe && b.unlocked ? (showcased ? "Remover da vitrine" : "Exibir na vitrine (ao lado do seu nome)") : b.description}
    >
      {showcased && <Star className="w-3.5 h-3.5 absolute top-2 left-2 text-primary fill-primary" data-testid={`showcase-star-${b.key}`} />}
      <div className={`text-3xl leading-none ${b.unlocked ? "" : "grayscale opacity-50"}`}>{b.icon}</div>
      <div className="text-xs font-semibold leading-tight">{b.title}</div>
      <div className="text-[10px] text-muted-foreground leading-tight">{b.description}</div>
      {!b.unlocked && (
        <>
          <Lock className="w-3 h-3 absolute top-2 right-2 text-muted-foreground" />
          <div className="w-full h-1.5 rounded-full bg-muted mt-1 overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round(b.progress * 100)}%` }} />
          </div>
          <div className="text-[10px] text-muted-foreground">{b.value}/{b.target}</div>
        </>
      )}
    </div>
  );
}

/** Profile "Conquistas": unlocked badges, next milestones and the full grid grouped by category. */
export default function AchievementsPanel({ userId, isMe }) {
  const [data, setData] = useState(null);
  const [showcase, setShowcase] = useState([]);
  const [celebrate, setCelebrate] = useState(new Set());
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/users/${userId}/achievements`).then((r) => {
      setData(r.data);
      setShowcase(r.data.showcase_badges || []);
      const param = new URLSearchParams(window.location.search).get("celebrate");
      const fresh = new Set([...(r.data.newly_unlocked || []), ...(param ? [param] : [])].filter(Boolean));
      if (fresh.size && isMe) {
        setCelebrate(fresh);
        fireConfetti();
        const first = r.data.badges.find((b) => fresh.has(b.key));
        if (first) toast.success(`${first.icon} Nova conquista: ${first.title}!`, { description: first.description });
        setTimeout(() => setCelebrate(new Set()), 6000);
      }
    }).catch(() => setData(null));
  }, [userId, isMe]);

  const toggleShowcase = async (key) => {
    const next = showcase.includes(key) ? showcase.filter((k) => k !== key) : [...showcase, key];
    if (next.length > 3) { toast.info("A vitrine mostra até 3 conquistas — remova uma para adicionar outra."); return; }
    try {
      const r = await axios.put(`${API}/users/me/showcase-badges`, { badges: next });
      setShowcase(r.data.showcase_badges.map((b) => b.key));
      toast.success(next.length ? "Vitrine atualizada — aparece ao lado do seu nome nos posts e comentários" : "Vitrine limpa");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar vitrine"); }
  };
  if (!data) return null;

  const groups = data.badges.reduce((acc, b) => { (acc[b.category_label] ||= []).push(b); return acc; }, {});
  const since = data.member_since ? new Date(data.member_since).toLocaleDateString("pt-BR", { month: "long", year: "numeric" }) : null;

  return (
    <div className="space-y-4" data-testid="achievements-panel">
      <Card className="p-5 bg-gradient-to-br from-amber-500/10 via-background to-primary/5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-600 flex items-center justify-center"><Trophy className="w-6 h-6" /></div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-bold text-lg" data-testid="achievements-summary">{data.unlocked_count} de {data.total} conquistas</div>
            <div className="text-xs text-muted-foreground">
              {since ? `Membro desde ${since} · ` : ""}{data.days_on_platform} dia{data.days_on_platform === 1 ? "" : "s"} na plataforma · {data.score.toLocaleString("pt-BR")} pts
            </div>
          </div>
          <div className="w-full sm:w-56">
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.round((data.unlocked_count / data.total) * 100)}%` }} /></div>
          </div>
        </div>
        {data.next_up?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{isMe ? "Próximas conquistas" : "Quase lá"}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="achievements-next-up">
              {data.next_up.map((b) => (
                <div key={b.key} className="flex items-center gap-2 p-2 rounded-xl bg-background/70 border border-border" data-testid={`next-${b.key}`}>
                  <span className="text-xl grayscale opacity-70">{b.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">{b.title}</div>
                    <div className="h-1 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.round(b.progress * 100)}%` }} /></div>
                    <div className="text-[10px] text-muted-foreground">{b.value}/{b.target}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {isMe && (
        <Card className="p-3 flex items-center gap-3 flex-wrap" data-testid="showcase-card">
          <Sparkles className="w-4 h-4 text-primary" />
          <div className="text-sm"><b>Vitrine de badges</b> <span className="text-muted-foreground text-xs">— clique em até 3 conquistas desbloqueadas para exibi-las ao lado do seu nome</span></div>
          <div className="ml-auto flex items-center gap-1 text-lg" data-testid="showcase-preview">
            {showcase.length === 0 && <span className="text-xs text-muted-foreground">nenhuma selecionada</span>}
            {showcase.map((k) => { const b = data.badges.find((x) => x.key === k); return b ? <span key={k} title={b.title}>{b.icon}</span> : null; })}
          </div>
        </Card>
      )}
      {Object.entries(groups).map(([label, list]) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{label}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {list.map((b) => <Badge key={b.key} b={b} isMe={isMe} showcased={showcase.includes(b.key)} onToggle={toggleShowcase} celebrate={celebrate.has(b.key)} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
